import { useEffect, useRef, useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Input } from "@/components/ui/input";
import { Panel, PanelHeader } from "@/components/ui/panel";
import { StatusBadge } from "@/components/status/status-badge";
import { ProviderLogo } from "@/components/providers/destination-provider-logo";
import { api } from "@/lib/api";
import {
  formatConnectedOn,
  type DestinationProvider,
  useIntegrationsOutlet,
} from "./integrations-shared";

export function IntegrationsDestinationsTab() {
  const { orgId, canManage, destinations, setMessage, refresh } =
    useIntegrationsOutlet();

  const [disconnectTarget, setDisconnectTarget] =
    useState<DestinationProvider | null>(null);
  const [disconnectBusy, setDisconnectBusy] = useState(false);
  const [connecting, setConnecting] = useState<DestinationProvider | null>(
    null,
  );
  const [secretsDraft, setSecretsDraft] = useState<Record<string, string>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [revealedSecrets, setRevealedSecrets] = useState<
    Record<string, boolean>
  >({});
  const [saveBusy, setSaveBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const destinationConnectPanelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!connecting) return;
    destinationConnectPanelRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
    });
  }, [connecting]);

  function openConnect(destination: DestinationProvider) {
    setMessage(null);
    setSaveError(null);
    setFieldErrors({});
    setSecretsDraft({});
    setRevealedSecrets({});
    setConnecting(destination);
  }

  function cancelConnect() {
    if (saveBusy) return;
    setConnecting(null);
    setSecretsDraft({});
    setFieldErrors({});
    setRevealedSecrets({});
    setSaveError(null);
  }

  async function saveDestinationCredentials() {
    if (!connecting) return;
    setFieldErrors({});
    setSaveError(null);

    const requiredFields = connecting.fields.filter((f) => f.required);
    const missing = requiredFields.filter((f) => !secretsDraft[f.key]?.trim());
    if (missing.length) {
      setFieldErrors(
        missing.reduce<Record<string, string>>((acc, f) => {
          acc[f.key] = `${f.label} is required.`;
          return acc;
        }, {}),
      );
      return;
    }

    setSaveBusy(true);
    try {
      const secrets: Record<string, string> = {};
      for (const f of connecting.fields) {
        const v = secretsDraft[f.key]?.trim();
        if (v) secrets[f.key] = v;
      }
      await api(
        `/api/accounts/${orgId}/providers/${encodeURIComponent(connecting.provider)}/setup`,
        {
          method: "PUT",
          body: JSON.stringify({ secrets }),
        },
      );
      setConnecting(null);
      setSecretsDraft({});
      setRevealedSecrets({});
      await refresh();
    } catch (e) {
      setSaveError(
        e instanceof Error ? e.message : "Could not save credentials",
      );
    } finally {
      setSaveBusy(false);
    }
  }

  async function disconnectDestination() {
    if (!disconnectTarget?.credentialId) return;
    setDisconnectBusy(true);
    setMessage(null);
    try {
      await api(
        `/api/accounts/${orgId}/credentials/${disconnectTarget.credentialId}`,
        {
          method: "DELETE",
        },
      );
      setDisconnectTarget(null);
      await refresh();
    } catch (e) {
      setMessage(
        e instanceof Error ? e.message : "Could not disconnect destination",
      );
    } finally {
      setDisconnectBusy(false);
    }
  }

  const connectRequired = connecting?.fields.filter((f) => f.required) ?? [];
  const connectFormValid = connectRequired.every((f) =>
    Boolean(secretsDraft[f.key]?.trim()),
  );
  const connectHints =
    connecting?.fields.filter((f) => Boolean(f.help?.trim())) ?? [];

  return (
    <>
      <Panel>
        <PanelHeader
          title="Destinations"
          description="CRM and other systems that receive registrant data"
        />

        {connecting ? (
          <div ref={destinationConnectPanelRef} className="space-y-4 p-5">
            <div>
              <h3 className="text-base font-semibold text-rs-ink">
                Enter {connecting.label} Credentials
              </h3>
              <p className="mt-1 text-sm text-rs-ink-muted">
                {connecting.description?.trim() ||
                  `Add the required account details for ${connecting.label}. Values are encrypted at rest and never shown in plaintext again.`}
              </p>
            </div>

            <form
              className="max-w-xl space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                void saveDestinationCredentials();
              }}
            >
              {connecting.fields.map((f) => {
                const errorForField = fieldErrors[f.key];
                const revealed = Boolean(revealedSecrets[f.key]);
                return (
                  <div key={f.key} className="space-y-1.5">
                    {f.secret ? (
                      <div className="relative">
                        <Input
                          name={f.key}
                          placeholder={f.label}
                          aria-label={f.label}
                          autoComplete="off"
                          type={revealed ? "text" : "password"}
                          value={secretsDraft[f.key] ?? ""}
                          disabled={saveBusy}
                          aria-invalid={Boolean(errorForField)}
                          className="pr-10"
                          onChange={(e) => {
                            const next = e.target.value;
                            setSecretsDraft((prev) => ({
                              ...prev,
                              [f.key]: next,
                            }));
                            setFieldErrors((prev) => {
                              if (!prev[f.key]) return prev;
                              const updated = { ...prev };
                              delete updated[f.key];
                              return updated;
                            });
                          }}
                        />
                        <button
                          type="button"
                          className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-rs-ink-subtle hover:text-rs-ink-muted disabled:opacity-50"
                          disabled={saveBusy}
                          onClick={() =>
                            setRevealedSecrets((prev) => ({
                              ...prev,
                              [f.key]: !prev[f.key],
                            }))
                          }
                          aria-label={
                            revealed ? "Hide Password" : "Show Password"
                          }
                          aria-pressed={revealed}
                        >
                          {revealed ? (
                            <EyeOff className="size-4" aria-hidden />
                          ) : (
                            <Eye className="size-4" aria-hidden />
                          )}
                        </button>
                      </div>
                    ) : (
                      <Input
                        name={f.key}
                        placeholder={f.label}
                        aria-label={f.label}
                        autoComplete="off"
                        type="text"
                        value={secretsDraft[f.key] ?? ""}
                        disabled={saveBusy}
                        aria-invalid={Boolean(errorForField)}
                        onChange={(e) => {
                          const next = e.target.value;
                          setSecretsDraft((prev) => ({
                            ...prev,
                            [f.key]: next,
                          }));
                          setFieldErrors((prev) => {
                            if (!prev[f.key]) return prev;
                            const updated = { ...prev };
                            delete updated[f.key];
                            return updated;
                          });
                        }}
                      />
                    )}
                    {errorForField ? (
                      <p role="alert" className="text-sm text-rs-danger">
                        {errorForField}
                      </p>
                    ) : null}
                  </div>
                );
              })}

              {saveError ? (
                <p role="alert" className="text-sm text-rs-danger">
                  {saveError}
                </p>
              ) : null}

              {connectHints.length > 0 ||
              connecting.recommendation ||
              connecting.docsUrl ? (
                <div className="space-y-3 text-sm text-rs-ink-muted">
                  {connectHints.length > 0 ? (
                    <div>
                      <p>Find these values in {connecting.label}:</p>
                      <ul className="mt-1.5 list-disc space-y-1 pl-5">
                        {connectHints.map((f) => (
                          <li key={f.key}>
                            {f.label}: {f.help}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                  {connecting.recommendation ? (
                    <p>{connecting.recommendation}</p>
                  ) : null}
                  {connecting.docsUrl ? (
                    <p>
                      <a
                        href={connecting.docsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-rs-accent hover:text-rs-accent-hover"
                      >
                        {connecting.docsLabel?.trim() ||
                          `View ${connecting.label} setup instructions`}{" "}
                        ↗
                      </a>
                    </p>
                  ) : null}
                </div>
              ) : null}

              <div className="flex justify-end gap-2 pt-1">
                <Button
                  type="button"
                  variant="secondary"
                  disabled={saveBusy}
                  onClick={cancelConnect}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={!connectFormValid || saveBusy}>
                  {saveBusy ? "Verifying Credentials…" : "Save Credentials"}
                </Button>
              </div>
            </form>
          </div>
        ) : destinations.length === 0 ? (
          <div className="px-5 py-8 text-center text-sm text-rs-ink-muted">
            No destination providers are available yet.
          </div>
        ) : (
          <div className="flex flex-col gap-3 p-5">
            {destinations.map((d) => (
              <div
                key={d.provider}
                className="flex w-full flex-col gap-4 rounded-rs border border-rs-border bg-rs-bg p-4 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="flex min-w-0 items-start gap-3">
                  <ProviderLogo provider={d.provider} label={d.label} />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-medium text-rs-ink">{d.label}</p>
                      {d.status === "beta" ? (
                        <StatusBadge tone="needs_configuration">
                          Beta
                        </StatusBadge>
                      ) : null}
                      {d.configured ? (
                        <StatusBadge tone="ready">Connected</StatusBadge>
                      ) : (
                        <StatusBadge tone="needs_configuration">
                          Not Connected
                        </StatusBadge>
                      )}
                    </div>
                    <div className="mt-1 text-sm text-rs-ink-muted">
                      {d.configured ? (
                        <ul className="space-y-0.5">
                          <li>
                            Connected on: {formatConnectedOn(d.connectedAt)}
                          </li>
                          {(d.credentialHints?.length
                            ? d.credentialHints
                            : d.hintMask
                              ? [
                                  {
                                    key: "hint",
                                    label: "Credentials",
                                    masked: d.hintMask,
                                  },
                                ]
                              : []
                          ).map((hint) => (
                            <li key={hint.key}>
                              {hint.label}: {hint.masked}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p>
                          {d.description || "Connect to sync registrant data."}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex shrink-0 justify-end gap-2 sm:pl-4">
                  {d.configured ? (
                    canManage ? (
                      <Button
                        size="sm"
                        variant="danger"
                        onClick={() => setDisconnectTarget(d)}
                      >
                        Disconnect
                      </Button>
                    ) : null
                  ) : canManage ? (
                    <Button size="sm" onClick={() => openConnect(d)}>
                      Connect
                    </Button>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>

      <ConfirmDialog
        open={!!disconnectTarget}
        onOpenChange={(open) => {
          if (!open && !disconnectBusy) setDisconnectTarget(null);
        }}
        title="Disconnect Destination"
        description={
          disconnectTarget
            ? `Remove saved credentials for ${disconnectTarget.label}? AttendeeSync will no longer be able to sync to this destination until you reconnect.`
            : null
        }
        impact="Stored API credentials for this organization will be deleted. Streams that use this destination will stop syncing until credentials are configured again."
        confirmLabel="Disconnect"
        dangerous
        busy={disconnectBusy}
        onConfirm={() => void disconnectDestination()}
      />
    </>
  );
}
